import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Shield, Users, Key, Activity, Check, X, Edit3, Save, RotateCcw, Inbox, Mail, Phone, Trash2, Building, MessageSquare, Workflow } from 'lucide-react';
import { api } from '@/utils/api';
import Layout from '@/components/Layout';

const ROLES = [
  { id: 'super_admin', name: 'Super Admin', name_ar: 'مدير عام', color: 'bg-red-100 text-red-700' },
  { id: 'platform_admin', name: 'Platform Admin', name_ar: 'مدير المنصة', color: 'bg-orange-100 text-orange-700' },
  { id: 'owner', name: 'Property Owner', name_ar: 'مالك عقار', color: 'bg-blue-100 text-blue-700' },
  { id: 'asset_manager', name: 'Asset Manager', name_ar: 'مدير أصول', color: 'bg-purple-100 text-purple-700' },
  { id: 'pmc', name: 'Property Management Co.', name_ar: 'شركة إدارة', color: 'bg-indigo-100 text-indigo-700' },
  { id: 'operations', name: 'Operations Manager', name_ar: 'مدير عمليات', color: 'bg-teal-100 text-teal-700' },
  { id: 'maintenance', name: 'Maintenance Staff', name_ar: 'فريق الصيانة', color: 'bg-yellow-100 text-yellow-700' },
  { id: 'finance', name: 'Finance Manager', name_ar: 'مدير مالي', color: 'bg-green-100 text-green-700' },
  { id: 'marketing', name: 'Marketing Agent', name_ar: 'وكيل تسويق', color: 'bg-pink-100 text-pink-700' },
  { id: 'enterprise', name: 'Enterprise Admin', name_ar: 'مدير مؤسسي', color: 'bg-slate-100 text-slate-700' },
  { id: 'tenant', name: 'Tenant', name_ar: 'مستأجر', color: 'bg-cyan-100 text-cyan-700' },
  { id: 'investor', name: 'Investor (Read-only)', name_ar: 'مستثمر (قراءة)', color: 'bg-amber-100 text-amber-700' },
];

const PERMISSIONS = [
  { id: 'properties', name: 'Properties', name_ar: 'العقارات' },
  { id: 'units', name: 'Units', name_ar: 'الوحدات' },
  { id: 'tenants', name: 'Tenants', name_ar: 'المستأجرين' },
  { id: 'leases', name: 'Leases', name_ar: 'العقود' },
  { id: 'finance', name: 'Finance', name_ar: 'المالية' },
  { id: 'maintenance', name: 'Maintenance', name_ar: 'الصيانة' },
  { id: 'marketing', name: 'Marketing', name_ar: 'التسويق' },
  { id: 'ai', name: 'AI Insights', name_ar: 'الذكاء الاصطناعي' },
  { id: 'reports', name: 'Reports', name_ar: 'التقارير' },
  { id: 'settings', name: 'Settings', name_ar: 'الإعدادات' },
  { id: 'admin', name: 'Admin Panel', name_ar: 'لوحة الإدارة' },
];

// Default Role-permission matrix
const DEFAULT_ROLE_PERMISSIONS = {
  super_admin: ['properties', 'units', 'tenants', 'leases', 'finance', 'maintenance', 'marketing', 'ai', 'reports', 'settings', 'admin'],
  platform_admin: ['properties', 'units', 'tenants', 'leases', 'finance', 'maintenance', 'marketing', 'ai', 'reports', 'settings', 'admin'],
  owner: ['properties', 'units', 'tenants', 'leases', 'finance', 'maintenance', 'marketing', 'ai', 'reports', 'settings'],
  asset_manager: ['properties', 'units', 'tenants', 'leases', 'finance', 'maintenance', 'ai', 'reports'],
  pmc: ['properties', 'units', 'tenants', 'leases', 'maintenance', 'marketing', 'reports'],
  operations: ['properties', 'units', 'maintenance', 'reports'],
  maintenance: ['maintenance'],
  finance: ['finance', 'leases', 'reports'],
  marketing: ['marketing', 'units'],
  enterprise: ['properties', 'units', 'tenants', 'leases', 'finance', 'maintenance', 'marketing', 'ai', 'reports', 'settings', 'admin'],
  tenant: ['leases', 'maintenance'],
  investor: ['properties', 'units', 'finance', 'reports', 'ai'],
};

export default function Admin() {
  const { i18n } = useTranslation();
  const { user } = useAuth();
  const isAr = i18n.language === 'ar';
  const [activeView, setActiveView] = useState('roles');
  const [seedingRoles, setSeedingRoles] = useState(false);
  const [seedResult, setSeedResult] = useState(null);
  const [editingRole, setEditingRole] = useState(null);
  const [editPerms, setEditPerms] = useState([]);
  const [permissions, setPermissions] = useState(() => {
    try {
      const saved = localStorage.getItem('reva_role_permissions');
      return saved ? JSON.parse(saved) : DEFAULT_ROLE_PERMISSIONS;
    } catch { return DEFAULT_ROLE_PERMISSIONS; }
  });
  const [auditEvents, setAuditEvents] = useState([]);
  const [auditLoading, setAuditLoading] = useState(false);
  const [savedFlash, setSavedFlash] = useState('');
  const [leads, setLeads] = useState([]);
  const [leadCounts, setLeadCounts] = useState({ new: 0, contacted: 0, in_progress: 0, demo_scheduled: 0, closed: 0, lost: 0, total: 0 });
  const [leadFilter, setLeadFilter] = useState('all');
  const [leadsLoading, setLeadsLoading] = useState(false);
  const [selectedLead, setSelectedLead] = useState(null);
  const [leadNote, setLeadNote] = useState('');

  useEffect(() => {
    if (activeView === 'audit') fetchAudit();
    if (activeView === 'leads') fetchLeads();
  }, [activeView, leadFilter]);

  const fetchLeads = async () => {
    setLeadsLoading(true);
    try {
      const res = await api.listDemoRequests(leadFilter);
      setLeads(res.data.items || []);
      setLeadCounts(res.data.counts || {});
    } catch (e) { console.error(e); }
    finally { setLeadsLoading(false); }
  };

  // Update one or more fields on the selected request
  const updateLeadField = async (id, patch) => {
    try {
      const res = await api.updateDemoRequest(id, patch);
      setSelectedLead(prev => prev && prev.id === id ? { ...prev, ...res.data } : prev);
      setSavedFlash(isAr ? 'تم الحفظ' : 'Saved');
      fetchLeads();
      setTimeout(() => setSavedFlash(''), 2000);
    } catch (e) { console.error(e); }
  };

  // Append a timestamped note to the request
  const addCRMNote = async (id, text) => {
    if (!text || !text.trim()) return;
    try {
      const res = await api.addCRMNote(id, text.trim());
      setSelectedLead(prev => prev && prev.id === id
        ? { ...prev, notes: [...(prev.notes || []), res.data] }
        : prev);
      setLeadNote('');
      fetchLeads();
    } catch (e) { console.error(e); }
  };

  // Promote a CRM request to a follow-up task
  const convertToTask = async (id) => {
    try {
      const res = await api.convertCRMToTask(id, {});
      setSelectedLead(prev => prev && prev.id === id
        ? { ...prev, linked_task_id: res.data.task_id, status: 'in_progress' }
        : prev);
      setSavedFlash(isAr ? 'تم التحويل إلى مهمة' : 'Converted to task');
      fetchLeads();
      setTimeout(() => setSavedFlash(''), 2500);
    } catch (e) { console.error(e); alert(e.response?.data?.detail || 'Failed'); }
  };

  const deleteLead = async (id) => {
    if (!window.confirm(isAr ? 'حذف الطلب نهائيًّا؟' : 'Delete request permanently?')) return;
    try {
      await api.deleteDemoRequest(id);
      setSelectedLead(null);
      fetchLeads();
    } catch (e) { console.error(e); }
  };

  const fetchAudit = async () => {
    setAuditLoading(true);
    try {
      const res = await api.getAuditLog();
      setAuditEvents(res.data.events || []);
    } catch (e) { console.error(e); }
    finally { setAuditLoading(false); }
  };

  const openEdit = (roleId) => {
    setEditingRole(roleId);
    setEditPerms([...(permissions[roleId] || [])]);
  };

  const togglePerm = (perm) => {
    setEditPerms(prev => prev.includes(perm) ? prev.filter(p => p !== perm) : [...prev, perm]);
  };

  const savePerms = () => {
    const next = { ...permissions, [editingRole]: editPerms };
    setPermissions(next);
    localStorage.setItem('reva_role_permissions', JSON.stringify(next));
    setSavedFlash(isAr ? 'تم حفظ الصلاحيات' : 'Permissions saved');
    setEditingRole(null);
    setTimeout(() => setSavedFlash(''), 3000);
  };

  const resetPerms = () => {
    localStorage.removeItem('reva_role_permissions');
    setPermissions(DEFAULT_ROLE_PERMISSIONS);
    setSavedFlash(isAr ? 'تمت إعادة الصلاحيات الافتراضية' : 'Permissions reset to defaults');
    setTimeout(() => setSavedFlash(''), 3000);
  };

  const handleSeedRoles = async () => {
    setSeedingRoles(true);
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/seed/all-roles`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      const data = await res.json();
      setSeedResult(data);
    } catch (error) { console.error('Failed:', error); }
    finally { setSeedingRoles(false); }
  };

  // Mock fallback for empty audit
  const auditFallback = [
    { user: 'Ahmad Al-Rashidi', action_en: 'Added new property', action_ar: 'إضافة عقار جديد', time: new Date(Date.now() - 2*60*1000).toISOString(), type: 'create' },
    { user: 'Sarah', action_en: 'Updated lease contract', action_ar: 'تحديث عقد إيجار', time: new Date(Date.now() - 15*60*1000).toISOString(), type: 'update' },
    { user: 'System', action_en: 'Overdue payment alert', action_ar: 'إشعار دفعة متأخرة', time: new Date(Date.now() - 60*60*1000).toISOString(), type: 'alert' },
  ];
  const auditList = auditEvents.length > 0 ? auditEvents : auditFallback;

  return (
    <Layout>
      <div className="space-y-6">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-gray-900 rounded-xl flex items-center justify-center">
            <Shield className="h-5 w-5 text-white" strokeWidth={1.5} />
          </div>
          <div>
            <h1 className="font-heading text-2xl font-bold text-gray-900">{isAr ? 'لوحة الإدارة' : 'Admin Panel'}</h1>
            <p className="text-sm text-gray-500">{isAr ? 'إدارة الأدوار والصلاحيات وسجل المراجعة' : 'Manage roles, permissions, and audit trail'}</p>
          </div>
        </div>

        {/* View Toggle */}
        <div className="flex items-center justify-between">
          <div className="flex gap-2">
          {[
            { id: 'roles', icon: Users, label: isAr ? 'الأدوار' : 'Roles' },
            { id: 'permissions', icon: Key, label: isAr ? 'الصلاحيات' : 'Permissions Matrix' },
            { id: 'audit', icon: Activity, label: isAr ? 'سجل المراجعة' : 'Audit Log' },
            { id: 'leads', icon: Inbox, label: isAr ? 'مركز طلبات العملاء (CRM)' : 'CRM Requests', badge: leadCounts.new }
          ].map(v => {
            const Icon = v.icon;
            return (
              <button key={v.id} onClick={() => setActiveView(v.id)} className={`relative flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium transition-all ${activeView === v.id ? 'bg-gray-900 text-white' : 'bg-white text-gray-600 border border-gray-200 hover:border-gray-400'}`} data-testid={`admin-tab-${v.id}`}>
                <Icon className="h-4 w-4" strokeWidth={1.5} /> {v.label}
                {v.badge > 0 && <span className="ms-1 px-1.5 py-0.5 bg-red-500 text-white text-[10px] font-bold rounded-full">{v.badge}</span>}
              </button>
            );
          })}
        </div>
          <Button onClick={handleSeedRoles} disabled={seedingRoles} className="gradient-primary text-white rounded-xl" size="sm">
            <Users className="h-4 w-4 me-1.5" /> {seedingRoles ? '...' : (isAr ? 'إنشاء حسابات اختبار (12 دور)' : 'Seed All 12 Roles')}
          </Button>
        </div>

        {seedResult && (
          <Card className="shadow-soft border-green-200 bg-green-50">
            <CardContent className="pt-4 pb-4">
              <p className="text-sm font-medium text-green-800 mb-2">{seedResult.message}</p>
              <p className="text-xs text-green-700">{seedResult.note}</p>
              <div className="mt-2 grid grid-cols-2 md:grid-cols-3 gap-1">
                {seedResult.accounts?.map((acc, i) => (
                  <span key={i} className="text-xs bg-white px-2 py-1 rounded border">{acc.email} ({acc.role || acc.status})</span>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {savedFlash && <div className="text-sm text-green-700 bg-green-50 border border-green-100 px-4 py-2.5 rounded-xl" data-testid="admin-flash">{savedFlash}</div>}

        {/* Roles View */}
        {activeView === 'roles' && (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {ROLES.map(role => (
              <Card key={role.id} className="shadow-soft border-gray-100 hover:shadow-hover transition-all hover:-translate-y-0.5">
                <CardContent className="pt-5">
                  <div className="flex items-start justify-between mb-3">
                    <div className={`inline-block px-2.5 py-1 rounded-lg text-xs font-semibold ${role.color}`}>
                      {isAr ? role.name_ar : role.name}
                    </div>
                    <button onClick={() => openEdit(role.id)} className="text-gray-400 hover:text-primary p-1 rounded" data-testid={`role-edit-${role.id}`}>
                      <Edit3 className="h-3.5 w-3.5" />
                    </button>
                  </div>
                  <div className="text-xs text-gray-500">
                    {permissions[role.id]?.length || 0} {isAr ? 'صلاحية' : 'permissions'}
                  </div>
                  <div className="mt-3 flex flex-wrap gap-1">
                    {permissions[role.id]?.slice(0, 4).map(p => (
                      <span key={p} className="text-[10px] px-1.5 py-0.5 bg-gray-100 text-gray-500 rounded">{p}</span>
                    ))}
                    {(permissions[role.id]?.length || 0) > 4 && (
                      <span className="text-[10px] px-1.5 py-0.5 bg-primary/10 text-primary rounded">+{permissions[role.id].length - 4}</span>
                    )}
                  </div>
                  <button onClick={() => openEdit(role.id)} className="mt-4 w-full text-xs font-medium text-primary hover:bg-primary/5 py-2 rounded-lg transition-colors" data-testid={`role-manage-${role.id}`}>
                    {isAr ? 'إدارة الصلاحيات →' : 'Manage permissions →'}
                  </button>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Permissions Matrix */}
        {activeView === 'permissions' && (
          <>
            <div className="flex justify-end">
              <Button onClick={resetPerms} size="sm" variant="outline" className="rounded-xl" data-testid="admin-reset-perms">
                <RotateCcw className="h-3.5 w-3.5 me-1.5" /> {isAr ? 'استعادة الافتراضي' : 'Reset to defaults'}
              </Button>
            </div>
            <Card className="shadow-soft border-gray-100 overflow-hidden">
              <CardContent className="pt-6 overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="sticky start-0 bg-white z-10 min-w-[140px]">{isAr ? 'الدور' : 'Role'}</TableHead>
                      {PERMISSIONS.map(p => (
                        <TableHead key={p.id} className="text-center text-xs min-w-[80px]">{isAr ? p.name_ar : p.name}</TableHead>
                      ))}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {ROLES.map(role => (
                      <TableRow key={role.id}>
                        <TableCell className="sticky start-0 bg-white z-10 font-medium text-sm">
                          <div className="flex items-center gap-2">
                            <span className={`px-2 py-0.5 rounded text-xs ${role.color}`}>{isAr ? role.name_ar : role.name}</span>
                            <button onClick={() => openEdit(role.id)} className="text-gray-300 hover:text-primary"><Edit3 className="h-3 w-3" /></button>
                          </div>
                        </TableCell>
                        {PERMISSIONS.map(p => (
                          <TableCell key={p.id} className="text-center">
                            {permissions[role.id]?.includes(p.id)
                              ? <Check className="h-4 w-4 text-green-600 mx-auto" />
                              : <X className="h-4 w-4 text-gray-200 mx-auto" />}
                          </TableCell>
                        ))}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </>
        )}

        {/* Audit Log */}
        {activeView === 'audit' && (
          <Card className="shadow-soft border-gray-100">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="font-heading text-base">{isAr ? 'سجل الأنشطة' : 'Activity Log'}</CardTitle>
              <Button onClick={fetchAudit} size="sm" variant="outline" className="rounded-xl" data-testid="admin-audit-refresh">
                <RotateCcw className="h-3.5 w-3.5 me-1.5" /> {isAr ? 'تحديث' : 'Refresh'}
              </Button>
            </CardHeader>
            <CardContent>
              {auditLoading ? (
                <div className="text-center text-gray-500 py-6">{isAr ? 'جاري التحميل...' : 'Loading...'}</div>
              ) : (
                <div className="space-y-3" data-testid="admin-audit-list">
                  {auditList.map((entry, i) => (
                    <div key={i} className="flex items-center gap-4 p-3 bg-gray-50 rounded-xl">
                      <div className={`w-2 h-2 rounded-full ${entry.type === 'create' ? 'bg-green-500' : entry.type === 'alert' ? 'bg-red-500' : entry.type === 'admin' ? 'bg-purple-500' : 'bg-blue-500'}`} />
                      <div className="flex-1">
                        <div className="text-sm font-medium text-gray-900">{entry.user}</div>
                        <div className="text-xs text-gray-500">{isAr ? (entry.action_ar || entry.action) : (entry.action_en || entry.action)}</div>
                      </div>
                      <div className="text-xs text-gray-400">{entry.time ? new Date(entry.time).toLocaleString() : ''}</div>
                    </div>
                  ))}
                  {auditList.length === 0 && <div className="text-center text-sm text-gray-400 py-8">{isAr ? 'لا توجد أنشطة بعد' : 'No activity yet'}</div>}
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* CRM Requests Center */}
        {activeView === 'leads' && (
          <div className="space-y-4">
            {/* Status counts — 6 workflow stages */}
            <div className="grid grid-cols-2 md:grid-cols-7 gap-2">
              {[
                { key: 'all',             label: isAr ? 'الكل'             : 'All',             count: leadCounts.total,           color: 'bg-gray-50 text-gray-700' },
                { key: 'new',             label: isAr ? 'جديد'              : 'New',             count: leadCounts.new,             color: 'bg-blue-50 text-blue-700' },
                { key: 'contacted',       label: isAr ? 'تم التواصل'      : 'Contacted',       count: leadCounts.contacted,       color: 'bg-amber-50 text-amber-700' },
                { key: 'in_progress',     label: isAr ? 'قيد المتابعة'   : 'In progress',     count: leadCounts.in_progress,     color: 'bg-indigo-50 text-indigo-700' },
                { key: 'demo_scheduled',  label: isAr ? 'موعد محدد'       : 'Demo scheduled',  count: leadCounts.demo_scheduled,  color: 'bg-purple-50 text-purple-700' },
                { key: 'closed',          label: isAr ? 'مغلق'              : 'Closed',          count: leadCounts.closed,          color: 'bg-green-50 text-green-700' },
                { key: 'lost',            label: isAr ? 'فقد'               : 'Lost',            count: leadCounts.lost,            color: 'bg-red-50 text-red-700' },
              ].map(s => (
                <button key={s.key} onClick={() => setLeadFilter(s.key)} className={`p-2.5 rounded-xl border-2 text-start transition-all ${leadFilter === s.key ? 'border-primary bg-primary/5' : 'border-gray-100 bg-white hover:border-gray-200'}`} data-testid={`lead-filter-${s.key}`}>
                  <div className={`inline-block px-1.5 py-0.5 rounded text-[9.5px] font-bold uppercase tracking-wider ${s.color}`}>{s.label}</div>
                  <div className="text-xl font-heading font-bold text-gray-900 mt-1">{s.count || 0}</div>
                </button>
              ))}
            </div>

            <Card className="shadow-soft border-gray-100">
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="font-heading text-base flex items-center gap-2">
                  <Inbox className="h-4 w-4 text-primary" /> {isAr ? 'مركز طلبات العملاء (CRM)' : 'CRM Requests Center'} ({leads.length})
                </CardTitle>
                <Button onClick={fetchLeads} size="sm" variant="outline" className="rounded-xl" data-testid="leads-refresh">
                  <RotateCcw className="h-3.5 w-3.5 me-1.5" /> {isAr ? 'تحديث' : 'Refresh'}
                </Button>
              </CardHeader>
              <CardContent>
                {leadsLoading ? (
                  <div className="text-center text-gray-500 py-10">{isAr ? 'جاري التحميل...' : 'Loading...'}</div>
                ) : leads.length === 0 ? (
                  <div className="text-center py-12">
                    <div className="w-14 h-14 bg-blue-50 rounded-2xl flex items-center justify-center mx-auto mb-3">
                      <Inbox className="h-7 w-7 text-blue-400" strokeWidth={1.5} />
                    </div>
                    <p className="text-sm text-gray-500">{isAr ? 'لا توجد طلبات بعد' : 'No requests yet'}</p>
                  </div>
                ) : (
                  <div className="divide-y divide-gray-100" data-testid="leads-list">
                    {leads.map(lead => {
                      const statusColors = { new: 'bg-blue-100 text-blue-700', contacted: 'bg-amber-100 text-amber-700', in_progress: 'bg-indigo-100 text-indigo-700', demo_scheduled: 'bg-purple-100 text-purple-700', closed: 'bg-green-100 text-green-700', lost: 'bg-red-100 text-red-700' };
                      const statusLabels = isAr
                        ? { new: 'جديد', contacted: 'تم التواصل', in_progress: 'قيد المتابعة', demo_scheduled: 'موعد محدد', closed: 'مغلق', lost: 'فقد' }
                        : { new: 'New', contacted: 'Contacted', in_progress: 'In progress', demo_scheduled: 'Demo scheduled', closed: 'Closed', lost: 'Lost' };
                      const priorityColors = { high: 'text-red-600', medium: 'text-amber-600', low: 'text-gray-500' };
                      const priorityLabels = isAr ? { high: 'عالي', medium: 'متوسط', low: 'منخفض' } : { high: 'High', medium: 'Med', low: 'Low' };
                      const typeLabels = isAr ? { demo: 'عرض', contact: 'اتصال', access: 'وصول', inquiry: 'استفسار', support: 'دعم', other: 'آخر' } : { demo: 'Demo', contact: 'Contact', access: 'Access', inquiry: 'Inquiry', support: 'Support', other: 'Other' };
                      return (
                        <button key={lead.id} onClick={() => { setSelectedLead(lead); setLeadNote(''); }} className="w-full text-start py-3 hover:bg-gray-50 transition-colors rounded-xl px-3" data-testid={`lead-row-${lead.id}`}>
                          <div className="flex items-start justify-between gap-3">
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 mb-1 flex-wrap">
                                <span className="font-semibold text-gray-900">{lead.name}</span>
                                <span className={`text-[10px] font-bold uppercase px-1.5 py-0.5 rounded ${statusColors[lead.status] || statusColors.new}`}>{statusLabels[lead.status] || 'New'}</span>
                                <span className="text-[10px] font-medium uppercase px-1.5 py-0.5 rounded bg-gray-100 text-gray-700">{typeLabels[lead.request_type] || lead.request_type || 'demo'}</span>
                                <span className={`text-[10px] font-bold uppercase ${priorityColors[lead.priority] || priorityColors.medium}`}>● {priorityLabels[lead.priority] || 'Med'}</span>
                                {(lead.notes || []).length > 0 && <span className="text-[10px] text-gray-500">📝 {(lead.notes || []).length}</span>}
                                {lead.linked_task_id && <span className="text-[10px] text-green-600 font-medium">→ task</span>}
                              </div>
                              <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 text-xs text-gray-500">
                                <span className="flex items-center gap-1"><Mail className="h-3 w-3" />{lead.email}</span>
                                {lead.phone && <span className="flex items-center gap-1"><Phone className="h-3 w-3" />{lead.phone}</span>}
                                {lead.company && <span className="flex items-center gap-1"><Building className="h-3 w-3" />{lead.company}</span>}
                                {lead.assigned_to && <span className="px-1.5 py-0.5 bg-primary/10 text-primary rounded text-[10px] font-medium">@{lead.assigned_to.split('@')[0]}</span>}
                                {lead.source_page && <span className="px-1.5 py-0.5 bg-gray-50 rounded text-[10px] text-gray-600">{lead.source_page}</span>}
                              </div>
                              {lead.message && <p className="text-xs text-gray-600 mt-1.5 line-clamp-1">{lead.message}</p>}
                            </div>
                            <div className="text-[10px] text-gray-400 flex-shrink-0">{new Date(lead.created_at).toLocaleDateString()} · {new Date(lead.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</div>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        )}

        {/* CRM Request Detail Dialog */}
        <Dialog open={!!selectedLead} onOpenChange={(o) => !o && setSelectedLead(null)}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2"><Inbox className="h-5 w-5 text-primary" /> {isAr ? 'تفاصيل الطلب' : 'Request details'}</DialogTitle>
            </DialogHeader>
            {selectedLead && (
              <div className="space-y-4">
                <div className="p-4 bg-gray-50 rounded-xl space-y-2">
                  <div className="flex items-center justify-between gap-3 flex-wrap">
                    <div className="font-heading text-lg font-bold text-gray-900">{selectedLead.name}</div>
                    <span className="text-[10px] text-gray-500">{new Date(selectedLead.created_at).toLocaleString()}</span>
                  </div>
                  <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm text-gray-700">
                    <a href={`mailto:${selectedLead.email}`} className="flex items-center gap-1.5 hover:text-primary" data-testid="lead-email-link"><Mail className="h-3.5 w-3.5" />{selectedLead.email}</a>
                    {selectedLead.phone && <a href={`tel:${selectedLead.phone}`} className="flex items-center gap-1.5 hover:text-primary" data-testid="lead-phone-link"><Phone className="h-3.5 w-3.5" />{selectedLead.phone}</a>}
                  </div>
                  <div className="grid grid-cols-2 gap-3 pt-2 text-xs">
                    <div><span className="text-gray-400">{isAr ? 'الشركة' : 'Company'}:</span> <span className="text-gray-900 font-medium">{selectedLead.company || '—'}</span></div>
                    <div><span className="text-gray-400">{isAr ? 'حجم المحفظة' : 'Portfolio'}:</span> <span className="text-gray-900 font-medium">{selectedLead.portfolio_size || '—'}</span></div>
                    <div><span className="text-gray-400">{isAr ? 'النوع' : 'Type'}:</span> <span className="text-gray-900 font-medium">{selectedLead.request_type || 'demo'}</span></div>
                    <div><span className="text-gray-400">{isAr ? 'الصفحة' : 'Source'}:</span> <span className="text-gray-900 font-medium">{selectedLead.source_page || '—'}</span></div>
                  </div>
                </div>

                {selectedLead.message && (
                  <div>
                    <div className="text-[10px] uppercase font-bold text-gray-500 tracking-wider mb-1 flex items-center gap-1"><MessageSquare className="h-3 w-3" /> {isAr ? 'الرسالة' : 'Message'}</div>
                    <div className="p-3 bg-white border border-gray-100 rounded-xl text-sm text-gray-800 leading-relaxed">{selectedLead.message}</div>
                  </div>
                )}

                {/* Workflow controls — status / priority / assignee */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3 p-3 border border-gray-200 rounded-xl">
                  <div>
                    <label className="text-[10px] uppercase font-bold text-gray-500 tracking-wider block mb-1">{isAr ? 'الحالة' : 'Status'}</label>
                    <select value={selectedLead.status || 'new'} onChange={e => updateLeadField(selectedLead.id, { status: e.target.value })} className="w-full px-2 py-1.5 text-sm border border-gray-200 rounded-lg" data-testid="crm-status-select">
                      {['new','contacted','in_progress','demo_scheduled','closed','lost'].map(s => (
                        <option key={s} value={s}>{s.replace('_',' ')}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="text-[10px] uppercase font-bold text-gray-500 tracking-wider block mb-1">{isAr ? 'الأولوية' : 'Priority'}</label>
                    <select value={selectedLead.priority || 'medium'} onChange={e => updateLeadField(selectedLead.id, { priority: e.target.value })} className="w-full px-2 py-1.5 text-sm border border-gray-200 rounded-lg" data-testid="crm-priority-select">
                      {['high','medium','low'].map(p => <option key={p} value={p}>{p}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="text-[10px] uppercase font-bold text-gray-500 tracking-wider block mb-1">{isAr ? 'مسند إلى' : 'Assigned to'}</label>
                    <input type="email" value={selectedLead.assigned_to || ''} onChange={e => setSelectedLead({...selectedLead, assigned_to: e.target.value})} onBlur={e => updateLeadField(selectedLead.id, { assigned_to: e.target.value || null })} placeholder="email@..." className="w-full px-2 py-1.5 text-sm border border-gray-200 rounded-lg" data-testid="crm-assignee-input" />
                  </div>
                </div>

                {/* Notes timeline */}
                <div>
                  <div className="text-[10px] uppercase font-bold text-gray-500 tracking-wider mb-1.5 flex items-center justify-between">
                    <span className="flex items-center gap-1"><MessageSquare className="h-3 w-3" /> {isAr ? 'سجل الملاحظات' : 'Notes timeline'} ({(selectedLead.notes || []).length})</span>
                  </div>
                  {(selectedLead.notes || []).length > 0 && (
                    <div className="space-y-1.5 mb-2 max-h-40 overflow-y-auto">
                      {(selectedLead.notes || []).map(n => (
                        <div key={n.id} className="p-2 bg-amber-50/40 border-s-2 border-amber-300 rounded text-xs">
                          <div className="text-[10px] text-gray-500 mb-0.5">{n.by} · {new Date(n.at).toLocaleString()}</div>
                          <div className="text-gray-800">{n.text}</div>
                        </div>
                      ))}
                    </div>
                  )}
                  <div className="flex gap-2">
                    <textarea value={leadNote} onChange={e => setLeadNote(e.target.value)} rows={2} placeholder={isAr ? 'أضف ملاحظة...' : 'Add a note...'} className="flex-1 px-3 py-2 text-sm border border-gray-200 rounded-xl focus:outline-none focus:border-primary resize-none" data-testid="crm-add-note-input" />
                    <Button onClick={() => addCRMNote(selectedLead.id, leadNote)} size="sm" disabled={!leadNote.trim()} className="rounded-xl self-end" data-testid="crm-add-note-btn">{isAr ? 'إضافة' : 'Add'}</Button>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-2 pt-3 border-t">
                  <Button onClick={() => convertToTask(selectedLead.id)} size="sm" className="rounded-xl text-xs bg-primary text-white" data-testid="crm-convert-task" disabled={!!selectedLead.linked_task_id}>
                    <Workflow className="h-3.5 w-3.5 me-1.5" />
                    {selectedLead.linked_task_id ? (isAr ? 'تم التحويل ✓' : 'Converted ✓') : (isAr ? 'تحويل لمهمة' : 'Convert to task')}
                  </Button>
                  <Button onClick={() => deleteLead(selectedLead.id)} size="sm" variant="ghost" className="rounded-xl text-xs text-red-600" data-testid="lead-delete"><Trash2 className="h-3.5 w-3.5 me-1" /> {isAr ? 'حذف' : 'Delete'}</Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Edit Permissions Modal */}
        <Dialog open={!!editingRole} onOpenChange={(o) => !o && setEditingRole(null)}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>{isAr ? 'تعديل الصلاحيات' : 'Edit Permissions'} — {editingRole && (isAr ? ROLES.find(r => r.id === editingRole)?.name_ar : ROLES.find(r => r.id === editingRole)?.name)}</DialogTitle>
            </DialogHeader>
            {editingRole && (
              <div className="space-y-3">
                <p className="text-xs text-gray-500">{isAr ? 'فعّل أو عطّل الصلاحيات لهذا الدور:' : 'Enable or disable permissions for this role:'}</p>
                <div className="grid grid-cols-2 gap-2 max-h-[400px] overflow-y-auto">
                  {PERMISSIONS.map(p => {
                    const enabled = editPerms.includes(p.id);
                    return (
                      <button key={p.id} onClick={() => togglePerm(p.id)} className={`flex items-center gap-2 p-3 rounded-xl border-2 text-start transition-all ${enabled ? 'border-primary bg-primary/5' : 'border-gray-100 bg-white hover:border-gray-200'}`} data-testid={`perm-toggle-${p.id}`}>
                        <div className={`w-5 h-5 rounded-md flex items-center justify-center flex-shrink-0 ${enabled ? 'bg-primary text-white' : 'bg-gray-100'}`}>
                          {enabled && <Check className="h-3 w-3" />}
                        </div>
                        <div className="flex-1 text-sm font-medium text-gray-900">{isAr ? p.name_ar : p.name}</div>
                      </button>
                    );
                  })}
                </div>
                <div className="flex gap-3 pt-3 border-t">
                  <Button variant="outline" onClick={() => setEditingRole(null)} className="flex-1 rounded-xl">{isAr ? 'إلغاء' : 'Cancel'}</Button>
                  <Button onClick={savePerms} className="flex-1 gradient-primary text-white rounded-xl" data-testid="perm-save"><Save className="h-4 w-4 me-1.5" /> {isAr ? 'حفظ' : 'Save'}</Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}
